import type { AttachedFile } from "@/components/commons/file-upload-handler";

// Helper functions to read file content
export const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string || '');
    reader.onerror = (e) => reject(e);
    reader.readAsText(file);
  });
};

export const readFileAsDataURL = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string || '');
    reader.onerror = (e) => reject(e);
    reader.readAsDataURL(file);
  });
};

// Optimized base64 encoding similar to AI SDK approach
export const convertFileToBase64 = async (file: File): Promise<string> => {
  const arrayBuffer = await file.arrayBuffer();
  const uint8Array = new Uint8Array(arrayBuffer);
  
  // Convert Uint8Array to an array of characters
  const charArray = Array.from(uint8Array, byte => String.fromCharCode(byte));
  const binaryString = charArray.join('');
  const base64Data = btoa(binaryString);
  
  return base64Data;
};

// Upload file to S3
export const uploadFileToS3 = async (file: File, jwtToken: string): Promise<string> => {
  const formData = new FormData();
  formData.append("upload_file", file);
  formData.append("upload_file_type", "app_attachments");
  formData.append("upload_file_subtype", "prompt_files");
  formData.append("upload_file_title", file.name);

  const response = await fetch("/api/upload/s3", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${jwtToken}`,
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Failed to upload file to S3");
  }

  const data = await response.json();
  return data.url;
};

// Upload tenant logo to S3
export const uploadTenantLogo = async (file: File, tenantId: string, jwtToken: string): Promise<string> => {
  const formData = new FormData();
  formData.append("upload_file", file);
  formData.append("upload_file_type", tenantId);
  formData.append("upload_file_subtype", "logos");
  formData.append("upload_file_title", file.name);

  const response = await fetch("/api/upload/s3", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${jwtToken}`,
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Failed to upload tenant logo to S3");
  }

  const data = await response.json();
  return data.url;
};

// Check if file is an image
export const isImageFile = (type: string): boolean => {
  return type.startsWith('image/');
};

// Check if file is a text file
export const isTextFile = (type: string, name: string): boolean => {
  return type.startsWith('text/') || 
         type === 'application/json' || 
         type === 'application/javascript' ||
         name.endsWith('.txt') ||
         name.endsWith('.json') ||
         name.endsWith('.js') ||
         name.endsWith('.ts') ||
         name.endsWith('.tsx') ||
         name.endsWith('.jsx') ||
         name.endsWith('.css') ||
         name.endsWith('.html') ||
         name.endsWith('.md');
};

